import { maybeWebContext } from "../search";
// chat/brain.ts — the chat brain, SERVER-FIRST (mirrors study_companion/mood_tracker/recipe_box).
// askBrain sends the message + this app's declared action catalog to the generic fleet chat
// brain (POST /api/apps/solo_habit_tracker/chat) — ONE billed call returns ONE registered
// action + a reply; this file resolves habit-name references locally (the data lives here) and
// the app executes the action on-device. The original on-device regex router below
// (routeMessage) is kept as the FREE FALLBACK for offline / signed-out / out-of-funds — chat
// never goes dead. Original prototype notes:
//   • "add habit X [daily/weekly]"       → local parse            → add_habit
//   • "check in X" / "did X" / "X done"   → resolve id locally     → check_in
//   • "rename X to Y" / "make X weekly"   → resolve id locally     → edit_habit
//   • "delete X" / "remove X"             → resolve id locally     → delete_habit (UI confirms)
//   • "show daily / dormant habits"       → local filter           → filter
//   • "streak for X" / "how am I doing"   → local streak math      → streak
//   • "recap" / "insights" / "analyze"    → EXISTING /analyze call → recap (billed)
//   • anything else                       → none + a short nudge
//
// No sensitive domain fence needed here (habits are neutral, non-health, non-finance) — this
// app already stays OBSERVATIONAL by design (patterns/deltas only, see brain.ts header).

import { store, todayISO, type Cadence, type Habit, type HabitPatch } from "../store";
import { BrainError, brainCall } from "../brain";
import { findNamedMatches, notFoundMessage } from "./fuzzy";
import type { ChatAction, HabitRow, HabitFilter } from "./actions";
import { libraryFind, librarySummary } from "../library";
import { resolveWhen, splitTrailingWhen } from "./when";

export interface ChatTurn {
  role: "user" | "assistant";
  content: string;
  rows?: HabitRow[]; // assistant turns may carry filter matches to render inline
}
export interface BrainResponse {
  reply: string;         // conversational preface; the executor supplies the result line
  actions: ChatAction[]; // the shared brain returns an ARRAY — two asks in one message = two actions (G17).
}
/** One resolved action (what toAction/routeMessage/resolveEdit build); serverChat/askBrain assemble the array. */
interface One { reply: string; action: ChatAction }

// Sent to the shared chat brain as `scope` (server truncates ~400 chars). Habits are a neutral
// domain (no health/finance line), so this just keeps the reply channel in-scope + observational.
const SCOPE =
  "This app is a private habit tracker: add habits, check in, edit/search/delete them, show streaks, " +
  "and give an OBSERVATIONAL recap of the user's OWN logged habits (adherence, streaks, this-week-vs-last); " +
  "back up to their own cloud. Describe their logged data; don't diagnose, prescribe, or give health/medical advice.";

const prettyCadence = (c: string) =>
  c === "daily" ? "Daily" : c === "weekly" ? "Weekly" : c === "adhoc" ? "As needed" : c;

const HELP =
  "I can add habits, check you in, edit, search, show streaks, and recap — just tell me. Try:\n" +
  '• "add habit Morning walk daily"\n' +
  '• "check in Reading"\n' +
  '• "make Reading weekly" · "rename Reading to Read 20m"\n' +
  '• "show dormant habits" · "streak for Reading"\n' +
  '• "recap my habits" · "delete Reading"';

// ── Colour + cadence parsing (maps the app's own PALETTE names/hex) ────────────
const COLOR_NAMES: Record<string, string> = {
  rose: "#D6336C", pink: "#D6336C", red: "#D6336C",
  amber: "#F59E0B", orange: "#F59E0B", yellow: "#F59E0B", gold: "#F59E0B",
  green: "#10B981", emerald: "#10B981", mint: "#10B981",
  blue: "#3B82F6", sky: "#3B82F6", cyan: "#3B82F6",
  purple: "#8B5CF6", violet: "#8B5CF6", indigo: "#8B5CF6",
};
const COLOR_WORDS = "rose|pink|red|amber|orange|yellow|gold|green|emerald|mint|blue|sky|cyan|purple|violet|indigo";
const CADENCE_WORDS = "daily|every\\s*day|each\\s*day|weekly|every\\s*week|each\\s*week|adhoc|ad[-\\s]?hoc|as\\s*needed";

function parseColor(s: string): string | undefined {
  const hex = s.match(/#[0-9a-fA-F]{6}/);
  if (hex) return hex[0];
  const m = s.toLowerCase().match(new RegExp(`\\b(${COLOR_WORDS})\\b`));
  return m ? COLOR_NAMES[m[1]] : undefined;
}
function parseCadence(s: string): Cadence | undefined {
  const low = s.toLowerCase();
  if (/\b(daily|every\s*day|each\s*day)\b/.test(low)) return "daily";
  if (/\b(weekly|every\s*week|each\s*week)\b/.test(low)) return "weekly";
  if (/\b(adhoc|ad[-\s]?hoc|as\s*needed|whenever|sometimes|occasionally|flexible)\b/.test(low)) return "adhoc";
  return undefined;
}

const none = (message: string): One => ({ reply: "", action: { kind: "none", message } });

// ── Local habit resolution ─────────────────────────────────────────────────────
async function findByName(term: string): Promise<Habit[]> {
  const q = term.trim().toLowerCase().replace(/[.?!]+$/, "");
  if (!q) return [];
  const all = await store.listHabits();
  const exact = all.filter((h) => h.name.toLowerCase() === q);
  if (exact.length) return exact;
  const subs = all.filter((h) => h.name.toLowerCase().includes(q));
  if (subs.length) return subs;
  // Fleet FUZZY standard (fuzzy.ts): forgiving near-match so a mistyped/paraphrased habit resolves.
  return findNamedMatches(all, term); // Habit has `name`
}
/** "Couldn't find X" enriched with the closest habit names ("did you mean …?"). */
async function notFoundFor(term: string): Promise<string> {
  const all = await store.listHabits();
  return notFoundMessage(all, term);
}
function pickList(rows: Habit[]): string {
  return rows.slice(0, 5).map((h) => `• ${h.name} (${prettyCadence(h.cadence_target)})`).join("\n");
}

export async function askBrain(history: ChatTurn[], onChunk?: (t: string) => void): Promise<BrainResponse> {
  const last = [...history].reverse().find((t) => t.role === "user");
  const text = (last?.content ?? "").trim();
  if (!text) { const r = await routeMessage(text); return { reply: r.reply, actions: [r.action] }; }
  try {
    return await serverChat(history, text, onChunk);
  } catch (e) {
    // A money/auth/key refusal must be SAID, not masked: the local fallback answering a
    // funds-blocked question as if it were a search reads as a broken app (found live 2026-08-27).
    if (e instanceof BrainError && (e.code === "insufficient_funds" || e.code === "unauthenticated" || e.code === "managed_ai_blocked")) {
      return { reply: e.message, actions: [{ kind: "none" }] };
    }
    // Offline / transient server trouble → the free local intent-router below
    // still answers; chat never goes dead. (Local path handles one intent → one action.)
    const r = await routeMessage(text);
    return { reply: r.reply, actions: [r.action] };
  }
}

// ---- SERVER CHAT-BRAIN (the graduated path) --------------------------------
// The app declares WHAT it can do; the fleet chat brain decides WHICH action the
// message means; this file resolves references (habit name text → id) locally,
// because the data lives here.

// Standing instructions: the customer shapes the app through chat; stored on-device, sent every call.
const INSTRUCTIONS_KEY = "oriora.habit-tracker.instructions";
export const getInstructions = (): string => { try { return localStorage.getItem(INSTRUCTIONS_KEY) ?? ""; } catch { return ""; } };
export const addInstruction = (line: string): void => { try { const cur = getInstructions(); localStorage.setItem(INSTRUCTIONS_KEY, cur ? cur + "\n- " + line : "- " + line); } catch { /* ignore */ } };

export const CHAT_ACTIONS = [
  { name: "add_habit", description: "Create a new habit to track — one they want to DO, or one they want to STOP.", params: { name: "the habit's name", cadence: "optional cadence: daily, weekly, or adhoc (as-needed). Defaults to daily.", kind: "optional: \"break\" if this is something they want to STOP or cut down (smoking, late-night scrolling, biting nails); \"build\" otherwise. Defaults to build.", days: "optional specific weekdays, e.g. \"mon,wed,fri\" — use when they say which days", times_per_week: "optional number, when they say \"3 times a week\" without naming days", color: "optional colour name or #hex" } },
  { name: "check_in", description: "Mark a habit done — today, or on an EARLIER day they name. For a habit they are breaking, this records a SLIP. Identify the habit by name.", params: { name: "which habit — name as the user wrote it", when: "optional day, when they name one: \"yesterday\", \"sunday\", \"3 days ago\", or YYYY-MM-DD. Omit for today. Never a future day." } },
  { name: "edit_habit", description: "Rename a habit, change its cadence, or recolour it. Identify it by its CURRENT name.", params: { name: "which habit — current name as the user wrote it", new_name: "new name, only if renaming", cadence: "new cadence (daily/weekly/adhoc), only if changing", color: "new colour name or #hex, only if changing" } },
  { name: "delete_habit", description: "Delete one habit and its check-ins (the app asks the user to confirm first).", params: { name: "which habit — name as the user wrote it" } },
  { name: "filter", description: "Search / list the user's habits.", params: { text: "optional free text to match a habit name", cadence: "optional cadence to filter by: daily, weekly, or adhoc", state: "optional: active (checked in within 7 days) or dormant (not)" } },
  { name: "streak", description: "Show the current + best streak. Give a habit name for one habit, or leave blank for the whole portfolio summary.", params: { name: "optional — which habit; leave blank for a summary of all habits" } },
  { name: "recap", description: "Run the AI read of patterns across the user's habits (adherence, streaks, this-week-vs-last). No params.", params: {} },
  { name: "backup_save", description: "Back up the WHOLE collection to the customer's own GitHub or Google Drive (connected once in their Oriora account). Works for ANY size — if the backup is too large for the standard path, it saves straight from this device to their GitHub when that's logged in (backup menu), or the reply tells them the one-time login to enable it.", params: {} },
  { name: "backup_status", description: "Tell the customer WHERE their data is backed up: which cloud is connected in their Oriora account, which one new backups write to, and when the last backup was. Use for 'where is my data / is my stuff backed up / which cloud do you use'.", params: {} },
  { name: "backup_restore", description: "Restore from the customer's cloud backup — REPLACES current data (the app asks the user to confirm first). If they NAME a source ('restore from Drive'), pass provider.", params: { provider: "optional github|gdrive when the customer names which cloud to restore from" } },
  { name: "save_to_cloud", description: "Save a note, or THIS conversation, to the customer's OWN GitHub or Google Drive. Use when they ask to store/save/keep the conversation, their prompts/messages, or a note (NOT the whole collection — that's backup_save).", params: { content_kind: "\"conversation\" to save this whole chat, or \"note\" for a piece of text", note: "the text to save when content_kind is \"note\"", destination: "optional \"github\" or \"gdrive\" if the user names one; omit otherwise" } },
  { name: "save_local_copy", description: "Save a one-off copy of the user's data to a folder/location they pick on THIS device (the OS picker). Works on iPhone and Mac. Use for 'save my data to a file' / 'save a copy locally'.", params: {} },
  { name: "import_file", description: "Import / restore the user's data from a FILE they pick on this device — Files, iCloud, a folder, or a backup from another device. REPLACES current data (the app asks them to confirm first). Use for 'import my data', 'restore from a file', 'load my backup file'.", params: {} },
  { name: "remember", description: "Save a standing instruction for how THIS customer wants the app to behave from now on.", params: { instruction: "the preference, one sentence" } },
  // ── ARTIFACT TOOLS — the customer's own library (device-only). Whatever the AI
  // makes for them gets KEPT here instead of vanishing into the chat. Fleet-wide
  // capability, identical in every Oriora app.
  { name: "library_save", description: "Save a plan, summary, guide or note into the customer's ON-DEVICE library. Use whenever you produce something worth keeping, or they ask you to save/remember/write something down. When they ask you to RESEARCH something and make them a plan: write the FULL text in \`content\`, give it a clear title, and put a short summary in your reply.", params: { title: "short title", item_kind: "lesson | plan | note", content: "the FULL text to save — write it out completely", folder: "optional folder name" } },
  { name: "library_list", description: "List what the customer has saved in their library ('what have I saved', 'show my plans', 'what's in my library / in folder X').", params: { folder: "optional folder to filter by" } },
  { name: "library_remove", description: "Remove ONE library item by its title (the app asks the customer to confirm first). LIBRARY items only — the app's own entries have their own delete action.", params: { title: "which item (its title, from LIBRARY in the context)" } },
  { name: "library_move", description: "Move a library item into a folder ('move the summer plan into Plans').", params: { title: "which item", folder: "target folder name" } },
  { name: "library_merge", description: "Merge two or more library items into one (the app asks the customer to confirm first).", params: { titles: "the titles to merge, separated by |", new_title: "title for the merged item" } },
];

interface ServerAct { name?: string; action?: string; params?: Record<string, unknown> }
interface ServerChat { actions?: ServerAct[]; action?: string; params?: Record<string, unknown>; reply?: string }

async function serverChat(history: ChatTurn[], text: string, onChunk?: (t: string) => void): Promise<BrainResponse> {
  const habits = await store.listHabits();
  const list = habits.slice(0, 40).map((h) => `${h.name} (${prettyCadence(h.cadence_target)})`).join(", ");
  const _notes = getInstructions();
  const context = (`LIBRARY (their saved plans/notes): ${librarySummary()}. Habits: ${list || "(none yet)"}.`) + (_notes ? "\nCustomer's standing instructions (apply when filling params): " + _notes : "");
  const recent = history.slice(-21, -1).map((t) => ({ role: t.role, content: t.content }));
  const r = await brainCall<ServerChat>("chat", { message: text + (await maybeWebContext(text)), actions: CHAT_ACTIONS, context, history: recent, scope: SCOPE }, onChunk);
  const say = asStr(r.reply) ?? "";
  const raw: ServerAct[] = Array.isArray(r.actions) && r.actions.length
    ? r.actions
    : (r.action ? [{ action: r.action, params: r.params }] : []);   // back-compat / older server
  if (!raw.length) return { reply: say || HELP, actions: [{ kind: "none" }] };
  const actions: ChatAction[] = [];
  for (const a of raw) {
    const one = await toAction({ action: a.name ?? a.action ?? "", params: a.params, reply: "" });
    actions.push(one.action);
  }
  return { reply: say, actions };
}

const asStr = (v: unknown): string | undefined => (typeof v === "string" && v.trim() ? v.trim() : undefined);

export async function toAction(r: ServerChat): Promise<One> {
  const p = r.params ?? {};
  const say = asStr(r.reply) ?? "";

  // ── ARTIFACT TOOLS (the library) — handled EARLY, above any app-specific
  // precondition (no vehicle / no plant / nothing selected). The library belongs
  // to the customer, not to a currently-active record. (Garage shipped this bug:
  // its no-vehicle guard silently swallowed every library action, 2026-08-13.)
  if (r.action === "library_save") {
    const _content = asStr(p.content);
    if (!_content) return none("Tell me what to save — or ask me to write it first and I'll keep it.");
    const _rawKind = (asStr(p.item_kind) ?? "note").toLowerCase();
    const _itemKind = _rawKind === "lesson" || _rawKind === "plan" ? _rawKind : "note";
    return { reply: say, action: { kind: "library_save", title: asStr(p.title) ?? "Untitled", itemKind: _itemKind, content: _content, folder: asStr(p.folder) } };
  }
  if (r.action === "library_list") return { reply: say, action: { kind: "library_list", folder: asStr(p.folder) } };
  if (r.action === "library_remove") {
    const _r = resolveLibraryItem(asStr(p.title));
    if (!_r.item) return none(_r.problem!);
    return { reply: say, action: { kind: "library_remove", id: _r.item.id, label: `"${_r.item.title}"` } };
  }
  if (r.action === "library_move") {
    const _folder = asStr(p.folder);
    if (!_folder) return none("Which folder should it go to?");
    const _r2 = resolveLibraryItem(asStr(p.title));
    if (!_r2.item) return none(_r2.problem!);
    return { reply: say, action: { kind: "library_move", id: _r2.item.id, folder: _folder, label: `"${_r2.item.title}"` } };
  }
  if (r.action === "library_merge") {
    const _titles = (asStr(p.titles) ?? "").split("|").map((s) => s.trim()).filter(Boolean);
    if (_titles.length < 2) return none("Tell me which two (or more) items to merge, by title.");
    const _ids: string[] = [];
    for (const _t of _titles) {
      const _r3 = resolveLibraryItem(_t);
      if (!_r3.item) return none(_r3.problem!);
      _ids.push(_r3.item.id);
    }
    return { reply: say, action: { kind: "library_merge", ids: _ids, title: asStr(p.new_title) ?? _titles.join(" + "), label: _titles.map((t) => `"${t}"`).join(" + ") } };
  }
  switch (r.action) {
    case "add_habit": {
      const name = asStr(p.name);
      if (!name) return none(say || 'What should the habit be called? Try "add habit Morning walk daily".');
      const cadence = asStr(p.cadence) ? parseCadence(asStr(p.cadence)!) ?? "daily" : "daily";
      const color = asStr(p.color) ? parseColor(asStr(p.color)!) : undefined;
      const polarity = /^break$/i.test(asStr(p.kind) ?? "") ? "break" : "build";
      // Named weekdays beat a bare count: "Mon/Wed/Fri" is a stricter, more useful schedule than
      // "3 times a week", and the customer told us which days.
      const daysRaw = asStr(p.days);
      const timesRaw = asStr(p.times_per_week);
      let schedule: string | undefined;
      if (daysRaw) {
        const nums = parseWeekdays(daysRaw);
        if (nums.length) schedule = `days:${nums.join(",")}`;
      }
      if (!schedule && timesRaw) {
        const n = Number(String(timesRaw).match(/\d+/)?.[0]);
        if (n >= 1 && n <= 7) schedule = `count:${n}`;
      }
      return { reply: say, action: { kind: "add_habit", name, cadence_target: cadence, color, polarity, schedule } };
    }
    case "check_in": {
      const term = asStr(p.name);
      if (!term) return none(say || "Which habit did you check in? Give me its name.");
      const rows = await findByName(term);
      if (rows.length === 0) return none(`I couldn't find a habit matching "${term}". Add it first with "add habit ${term}".`);
      if (rows.length > 1) return none(`Which one?\n${pickList(rows)}`);
      // A named day is resolved HERE, against one consistent "today", and refused if it doesn't
      // resolve — rather than silently falling back to today and writing the mark on the wrong day.
      const whenRaw = asStr(p.when);
      const on = whenRaw ? resolveWhen(whenRaw, todayISO()) : todayISO();
      if (!on) {
        return none(`I couldn't work out which day "${whenRaw}" means. Try "yesterday", a weekday like "Sunday", "3 days ago", or a date like 2026-08-05.`);
      }
      return { reply: say, action: { kind: "check_in", id: rows[0].id, label: rows[0].name, on, polarity: rows[0].polarity } };
    }
    case "edit_habit": {
      const term = asStr(p.name);
      if (!term) return none(say || "Which habit do you want to edit? Give me its name.");
      const rows = await findByName(term);
      if (rows.length === 0) return none(await notFoundFor(term));
      if (rows.length > 1) return none(`I found ${rows.length} matching "${term}" — be more specific:\n${pickList(rows)}`);
      const h = rows[0];
      const patch: HabitPatch = {};
      const bits: string[] = [];
      const newName = asStr(p.new_name);
      if (newName) { patch.name = newName; bits.push(`renamed to "${newName}"`); }
      const cadStr = asStr(p.cadence);
      const cad = cadStr ? parseCadence(cadStr) : undefined;
      if (cad) { patch.cadence_target = cad; bits.push(`now ${prettyCadence(cad)}`); }
      const colorStr = asStr(p.color);
      const color = colorStr ? parseColor(colorStr) : undefined;
      if (color) { patch.color = color; bits.push("recoloured"); }
      if (Object.keys(patch).length === 0) return none(say || "What should I change on that habit?");
      return { reply: say, action: { kind: "edit_habit", id: h.id, patch, label: `${h.name}${bits.length ? ` — ${bits.join(", ")}` : ""}` } };
    }
    case "delete_habit": {
      const term = asStr(p.name);
      if (!term) return none(say || "Which habit do you want to delete? Give me its name.");
      const rows = await findByName(term);
      if (rows.length === 0) return none(await notFoundFor(term));
      if (rows.length > 1) return none(`I found ${rows.length} matching "${term}" — be more specific:\n${pickList(rows)}`);
      return { reply: say, action: { kind: "delete_habit", id: rows[0].id, label: rows[0].name } };
    }
    case "filter": {
      const filter: HabitFilter = {};
      const text = asStr(p.text);
      if (text) filter.text = text;
      const cadStr = asStr(p.cadence);
      const cad = cadStr ? parseCadence(cadStr) : undefined;
      if (cad) filter.cadence = cad;
      const stateStr = asStr(p.state)?.toLowerCase();
      if (stateStr === "active" || stateStr === "dormant") filter.state = stateStr;
      return { reply: "", action: { kind: "filter", filter, label: say || text || undefined } };
    }
    case "streak": {
      const term = asStr(p.name);
      if (!term) return { reply: say, action: { kind: "streak" } };
      const rows = await findByName(term);
      if (rows.length === 1) return { reply: say, action: { kind: "streak", id: rows[0].id, label: rows[0].name } };
      if (rows.length > 1) return none(`Which one?\n${pickList(rows)}`);
      return { reply: say, action: { kind: "streak" } }; // no match → portfolio summary
    }
    case "recap":
      return { reply: say, action: { kind: "recap" } };
    case "backup_save":
      return { reply: say, action: { kind: "backup_save" } };
    case "backup_status":
      return { reply: say, action: { kind: "backup_status" } };
    case "backup_restore": {
      const prov = asStr(p.provider);
      return { reply: say, action: { kind: "backup_restore", provider: prov === "github" || prov === "gdrive" ? prov : undefined } };
    }
    case "save_local_copy":
      return { reply: say, action: { kind: "save_local_copy" } };
    case "import_file":
      return { reply: say, action: { kind: "import_file" } };
    case "save_to_cloud": {
      const ck = asStr(p.content_kind) ?? "";
      const contentKind = /conversation|chat|messages|prompts/i.test(ck) ? "conversation" : "note";
      const dest = asStr(p.destination);
      const destination = dest ? (/drive|gdrive|google/i.test(dest) ? "gdrive" : /git\s*hub/i.test(dest) ? "github" : undefined) : undefined;
      return { reply: say, action: { kind: "save_to_cloud", contentKind, note: asStr(p.note) ?? asStr(p.content), destination } };
    }
    case "remember": {
      const instruction = asStr(p.instruction);
      if (!instruction) return none(say || "What should I remember?");
      return { reply: say, action: { kind: "remember", instruction } };
    }
    default:
      return none(say || HELP);
  }
}

export async function routeMessage(text: string): Promise<One> {
  const t = text.trim();
  if (!t) return none(HELP);
  const low = t.toLowerCase();

  // 1) ADD HABIT — requires the word "habit"; strips an optional TRAILING cadence and/or colour.
  const addM = t.match(/^(?:add|create|new|start|track)\s+(?:a\s+)?(?:new\s+)?habit\b[:\-\s]*([\s\S]+)$/i);
  if (addM) {
    let name = addM[1].trim();
    let cadence: Cadence = "daily";
    let color: string | undefined;
    // trailing "#hex" or "(in) <colour>"
    const colorTail = name.match(new RegExp(`(?:\\s+in)?\\s+(#[0-9a-fA-F]{6}|${COLOR_WORDS})\\s*$`, "i"));
    if (colorTail && colorTail.index !== undefined) { color = parseColor(colorTail[1]); name = name.slice(0, colorTail.index).trim(); }
    // trailing cadence
    const cadTail = name.match(new RegExp(`\\s*[,\\-]?\\s*(${CADENCE_WORDS})\\s*$`, "i"));
    if (cadTail && cadTail.index !== undefined) { cadence = parseCadence(cadTail[1]) ?? "daily"; name = name.slice(0, cadTail.index).trim(); }
    // trailing weekday list — "gym mon wed fri" / "gym on Monday and Thursday"
    let schedule: string | undefined;
    const dayTail = name.match(/\s+(?:on\s+|every\s+)?((?:mon|tue|tues|wed|weds|thu|thur|thurs|fri|sat|sun)[a-z]*(?:\s*(?:,|and|&|\s)\s*(?:mon|tue|tues|wed|weds|thu|thur|thurs|fri|sat|sun)[a-z]*)*)\s*$/i);
    if (dayTail && dayTail.index !== undefined) {
      const nums = parseWeekdays(dayTail[1]);
      if (nums.length) { schedule = `days:${nums.join(",")}`; name = name.slice(0, dayTail.index).trim(); }
    }
    // LEADING stop-language makes this a habit to BREAK. "quit smoking" is how people actually say
    // it, and taking it literally as a build habit would mean a mark = "I quit smoking today",
    // which is the opposite of what they will tap. The verb is stripped so the row reads
    // "Smoking · stayed clean" rather than the double-negative "Quit smoking · stayed clean".
    let polarity = "build";
    const stopM = name.match(/^(?:stop|quit|give\s+up|cut\s+down\s+on|cut\s+out|no\s+more|avoid)\s+([\s\S]+)$/i);
    if (stopM) {
      polarity = "break";
      const rest = stopM[1].trim().replace(/^(?:on\s+|my\s+|the\s+)/i, "");
      name = rest.charAt(0).toUpperCase() + rest.slice(1);
    }
    name = name.replace(/[,:\-]+$/, "").trim();
    if (!name) return none('What should the habit be called? e.g. "add habit Morning walk daily".');
    return { reply: "", action: { kind: "add_habit", name, cadence_target: cadence, color, polarity, schedule } };
  }

  // 2) DELETE — resolve the target locally; the UI asks for confirmation before executing.
  const delM = t.match(/^(?:delete|remove|drop)\s+(?:the\s+)?(?:habit\s+)?([\s\S]+)$/i);
  if (delM) {
    const term = delM[1].trim();
    const rows = await findByName(term);
    if (rows.length === 0) return none(await notFoundFor(term));
    if (rows.length > 1) return none(`I found ${rows.length} matching "${term}" — be more specific:\n${pickList(rows)}`);
    return { reply: "", action: { kind: "delete_habit", id: rows[0].id, label: rows[0].name } };
  }

  // 3) EDIT — rename / change cadence / recolour. Returns null when it isn't an edit.
  const edit = await routeEdit(t);
  if (edit) return edit;

  // 4) CHECK IN — "check in X", "did X", "mark X done", "X done".
  const checkM =
    t.match(/^(?:check\s*[-]?\s*in|checkin|log|logged)\s+(?:to\s+|for\s+|my\s+|the\s+|on\s+)?([\s\S]+)$/i)
    || t.match(/^(?:i\s+)?(?:did|finished|completed)\s+(?:my\s+|the\s+)?([\s\S]+)$/i)
    || t.match(/^mark\s+(?:my\s+|the\s+)?([\s\S]+?)\s+(?:as\s+)?(?:done|complete|completed|checked)\s*$/i)
    || t.match(/^([\s\S]+?)\s+(?:is\s+)?(?:done|complete|completed)\s*$/i);
  if (checkM) {
    // Peel a trailing day off before the name is matched — see splitTrailingWhen for why.
    const today = todayISO();
    const { name: term, on } = splitTrailingWhen(checkM[1].trim(), today);
    if (!term) return none("Which habit? Give me its name.");
    const rows = await findByName(term);
    if (rows.length === 0) return none(`I couldn't find a habit matching "${term}". Add it first with "add habit ${term}".`);
    if (rows.length > 1) return none(`Which one?\n${pickList(rows)}`);
    return { reply: "", action: { kind: "check_in", id: rows[0].id, label: rows[0].name, on: on ?? today, polarity: rows[0].polarity } };
  }

  // 5) STREAK for a named habit — "streak for X" / "X streak".
  const streakM = t.match(/^(?:streaks?)\s+(?:for\s+|on\s+)?([\s\S]+)$/i) || t.match(/^([\s\S]+?)\s+streaks?\s*$/i);
  if (streakM) {
    const term = streakM[1].trim();
    if (/^(my|all|the|habits?)$/i.test(term)) return { reply: "", action: { kind: "streak" } };
    const rows = await findByName(term);
    if (rows.length === 1) return { reply: "", action: { kind: "streak", id: rows[0].id, label: rows[0].name } };
    if (rows.length > 1) return none(`Which one?\n${pickList(rows)}`);
    return { reply: "", action: { kind: "streak" } }; // no match → portfolio summary
  }

  // 6) RECAP — the AI read of patterns via the EXISTING /analyze endpoint (billed downstream).
  if (/\b(recap|insights?|analy[sz]e|analysis|patterns?|trends?|what\s+patterns)\b/i.test(low)) {
    return { reply: "", action: { kind: "recap" } };
  }

  // 7) SUMMARY — portfolio streak overview (local, free).
  if (/\b(summary|summar(?:ise|ize)|overview|how\s+am\s+i\s+doing|how\s+are\s+my\s+habits|my\s+streaks?)\b/i.test(low)) {
    return { reply: "", action: { kind: "streak" } };
  }

  // 8) FILTER / SEARCH — query-shaped, or any leftover mention of "habits" → local list.
  if (/\b(show|list|find|search|which|what|display|see)\b/i.test(low) || /\bhabits?\b/i.test(low)) {
    const filter: HabitFilter = {};
    const cad = parseCadence(low);
    if (cad) filter.cadence = cad;
    if (/\b(dormant|inactive|stalled|neglected|dropped|abandoned|slipping)\b/i.test(low)) filter.state = "dormant";
    else if (/\b(active|ongoing|current|going|kept\s*up)\b/i.test(low)) filter.state = "active";
    return { reply: "", action: { kind: "filter", filter, label: t } };
  }

  // 9) Unclear → a short clarifying nudge.
  return none(HELP);
}

// EDIT intents. Returns null if the message isn't an edit so the router can fall through.
async function routeEdit(t: string): Promise<One | null> {
  // "rename X to Y" / "change/set X's name to Y"
  let m = t.match(/^rename\s+(.+?)\s+to\s+(.+)$/i) || t.match(/^(?:change|set)\s+(.+?)(?:'s)?\s+name\s+to\s+(.+)$/i);
  if (m) {
    const newName = m[2].trim().replace(/^["']|["'.?!]+$/g, "").trim();
    if (!newName) return null;
    return resolveEdit(m[1], { name: newName }, `renamed to "${newName}"`);
  }

  // Cadence: "change/set/make/switch X (to) daily|weekly|adhoc" / "mark X as weekly"
  m = t.match(new RegExp(`^(?:change|set|make|switch)\\s+(.+?)\\s+(?:to\\s+|cadence\\s+to\\s+|as\\s+)?(${CADENCE_WORDS})\\s*$`, "i"))
    || t.match(new RegExp(`^mark\\s+(.+?)\\s+as\\s+(${CADENCE_WORDS})\\s*$`, "i"));
  if (m) {
    const cad = parseCadence(m[2]);
    if (!cad) return null;
    return resolveEdit(m[1], { cadence_target: cad }, `now ${prettyCadence(cad)}`);
  }

  // Colour: "change/set X('s) colour (to) blue" / "make X blue"
  m = t.match(new RegExp(`^(?:change|set)\\s+(.+?)(?:'s)?\\s+colou?r\\s+(?:to\\s+)?(#[0-9a-fA-F]{6}|${COLOR_WORDS})\\s*$`, "i"))
    || t.match(new RegExp(`^make\\s+(.+?)\\s+(#[0-9a-fA-F]{6}|${COLOR_WORDS})\\s*$`, "i"));
  if (m) {
    const color = parseColor(m[2]);
    if (!color) return null;
    return resolveEdit(m[1], { color }, "recoloured");
  }

  return null;
}

/** "mon,wed,fri" / "Monday and Thursday" → [1,3,5]. Order-stable, de-duplicated. */
export function parseWeekdays(raw: string): number[] {
  const MAP: Record<string, number> = {
    sun: 0, mon: 1, tue: 2, tues: 2, wed: 3, weds: 3, thu: 4, thur: 4, thurs: 4, fri: 5, sat: 6,
  };
  const out: number[] = [];
  for (const tok of raw.toLowerCase().split(/[^a-z]+/).filter(Boolean)) {
    const key = Object.keys(MAP).find((k) => tok.startsWith(k));
    if (key && !out.includes(MAP[key])) out.push(MAP[key]);
  }
  return out.sort((a, b) => a - b);
}

async function resolveEdit(term: string, patch: HabitPatch, note?: string): Promise<One> {
  const rows = await findByName(term);
  if (rows.length === 0) return none(await notFoundFor(term));
  if (rows.length > 1) return none(`I found ${rows.length} matching "${term.trim()}" — be more specific:\n${pickList(rows)}`);
  const h = rows[0];
  const label = note ? `${h.name} — ${note}` : h.name;
  return { reply: "", action: { kind: "edit_habit", id: h.id, patch, label } };
}

// Resolve a library title to exactly one item, or explain why not (same 0/1/many
// contract the app uses for its own entries).
function resolveLibraryItem(term: string | undefined): { item?: import("../library").LibraryItem; problem?: string } {
  if (!term) return { problem: "Which saved item do you mean? Give me its title." };
  const found = libraryFind(term);
  if (found.length === 0) return { problem: `I couldn't find "${term}" in your library.` };
  if (found.length > 1) return { problem: `I found ${found.length} matching "${term}" — be more specific: ${found.map((f) => `"${f.title}"`).join(", ")}` };
  return { item: found[0] };
}
